import type {
  CallAppRequest,
  OpenAppViewRequest,
  OpenAppViewResponse,
  PluginListingEntry,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { RemotePageTarget } from '@crowdex/plugin-contracts';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';

const JSON_HEADERS = { 'content-type': 'application/json' } as const;

export function listApps(): Promise<PluginListingEntry[]> {
  return apiFetch('/api/plugins', { cache: 'no-store' });
}

/** The skills of the account's enabled apps, as the composer offers them. */
export async function listAppSkills(): Promise<AvailablePluginSkill[]> {
  const skills = await apiFetch<
    { app: string; appTitle: string; name: string; description: string; uri: string }[]
  >('/api/plugins/skills', { cache: 'no-store' });
  return skills.map((skill) => ({
    name: skill.name,
    description: skill.description,
    path: skill.uri,
    pluginName: skill.app,
    pluginDisplayName: skill.appTitle,
    uri: skill.uri,
  }));
}

export function openAppView(input: OpenAppViewRequest): Promise<OpenAppViewResponse> {
  return apiFetch('/api/apps/views', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

/** Calls a route of an app for what the core renders (a sidebar, an action's state). */
export function callApp(input: CallAppRequest): Promise<unknown> {
  return apiFetch('/api/apps/call', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

/**
 * Where an app's remote page (`page.url`) opens for this user; `signIn` also asks for a one-time
 * URL opening it signed in.
 */
export function appRemotePage(
  name: string,
  signIn = false,
  path?: string | null,
): Promise<RemotePageTarget> {
  const query = new URLSearchParams();
  if (signIn) query.set('sign_in', '1');
  if (path) query.set('path', path);
  return apiFetch(
    `/api/plugins/${encodeURIComponent(name)}/remote-page${query.size > 0 ? `?${query}` : ''}`,
    { cache: 'no-store' },
  );
}
